// "Update now" (F-STN-12, spec/10 §8.2, spec/13 §3.3; the owner, 2026-10-07: "Can we have an option on app.clavian.io
// to update manually?"). A PC installs a new Clavian release by itself while it is idle; with `stations:maintenance`,
// staff may ask one online PC to check for the latest release and install it now — and never during a session: with
// one running it installs the moment the session ends.
//
// The action sits where the PC's client is shown (the station modal's Station client section). It confirms first, in
// place inside the station modal — a dialog never opens a dialog (spec/13 §2) — saying what will happen: it checks and
// installs now; a running session finishes first; the software restarts for about a minute. Then it sends
// `POST …/stations/{s}/client-update/install-now` and follows the
// `station.client.update` command to the station's answer over this member's `command.updated`: "Up to date",
// "Updating to 1.10.8400.0", "installs as soon as the session ends", or why it cannot. Meanwhile the client list is read
// every few seconds, so the status row moves through downloading and installing as the PC reports it.
//
// A diskless PC (CCBoot, 2026-10-07, spec/10 §14) does not install updates itself: what it installs is lost when it
// restarts. There the action is **Update the image**: its confirm says to start the PC as CCBoot super client first and to
// save the image afterwards, and it sends `reason: update_image`, so the PC treats this boot as the image's maintenance boot
// and installs into the image.

import type { FloorStationState } from '@clavian/api-client';
import type { CommandUpdatedPayload } from '@clavian/contracts/realtime';
import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useCommandUpdate } from '../org/printing/command-status';
import { useRealtimeLive } from '../realtime';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { Notice, type NoticeTone } from '../ui';
import { type UpdateNowReason, useUpdateStationNow } from './queries';

import './update-now.css';

/** The modes in which a session is running (spec/10 §6.1): a deferred install waits for it to end. */
const SESSION_MODES: ReadonlySet<string> = new Set(['in_session', 'session_locked', 'offline_locked']);

/** What a sent Update now says: the copy key under `stations.client.updateNow.progress`, its values and tone. */
export interface UpdateNowProgress {
  readonly key: string;
  readonly version?: string | null;
  readonly detail?: string | null;
  readonly tone: NoticeTone;
  /** Still moving: keep reading the client list every few seconds. */
  readonly following: boolean;
}

function stringOf(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * The progress of a sent Update now, from its command's last `command.updated` (null: none heard yet). Pure. An acked
 * command carries the station's `StationClientUpdateCommandResult`; a failed one its error `{ code, message }`.
 */
export function updateNowProgress(update: CommandUpdatedPayload | null, live: boolean): UpdateNowProgress {
  if (update === null || update.status === 'queued' || update.status === 'sent') {
    return { key: live ? 'sent' : 'sentOffline', tone: 'info', following: true };
  }
  if (update.status === 'expired') return { key: 'expired', tone: 'warn', following: false };
  const result = update.result ?? {};
  if (update.status === 'failed') {
    const code = stringOf(result['code']);
    const detail = stringOf(result['message']) ?? code;
    switch (code) {
      case 'update_refused':
        return { key: 'refused', detail, tone: 'warn', following: false };
      case 'check_failed':
        return { key: 'checkFailed', tone: 'warn', following: false };
      case 'offline':
        return { key: 'offline', tone: 'warn', following: false };
      case 'unsupported':
        return { key: 'unsupported', tone: 'warn', following: false };
      default:
        return { key: 'failed', detail, tone: 'danger', following: false };
    }
  }
  const target = stringOf(result['targetVersion']);
  switch (result['outcome']) {
    case 'up_to_date':
      return { key: 'upToDate', version: stringOf(result['clientVersion']), tone: 'ok', following: false };
    case 'started':
      return { key: target === null ? 'startedNoVersion' : 'started', version: target, tone: 'info', following: true };
    case 'deferred':
      return { key: SESSION_MODES.has(String(result['mode'])) ? 'deferredSession' : 'deferredBusy', version: target, tone: 'info', following: true };
    default:
      // `checking`, or an outcome a newer station sends: the status row says what it does.
      return { key: 'checking', tone: 'info', following: true };
  }
}

/** One station's Update now: the dialog's state, the send, and the command it follows. */
export function useUpdateNow(station: FloorStationState) {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const send = useUpdateStationNow(venue.organizationId, venue.id);
  const live = useRealtimeLive();
  const [open, setOpen] = useState(false);
  // Install on this PC, or — a diskless PC started as CCBoot super client — into the image.
  const [reason, setReason] = useState<UpdateNowReason>('install_now');
  // The action, so focus goes back to it when the in-place confirm closes.
  const triggerId = useId();
  const backToAction = () => window.setTimeout(() => document.getElementById(triggerId)?.focus(), 0);
  const [commandId, setCommandId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const update = useCommandUpdate(venue.id, commandId);
  const name = station.name ?? t('floor.tile.numbered', { number: station.number });
  const progress = commandId === null ? null : updateNowProgress(update, live);

  const confirm = () => {
    setError(null);
    send.mutate({ stationId: station.stationId, reason }, {
      onSuccess: (command) => {
        setCommandId(command.id);
        setOpen(false);
        backToAction();
      },
      onError: (failure) =>
        setError(
          problemCode(failure) === PROBLEM.conflict
            ? t('stations.client.updateNow.refused.conflict', { name })
            : problem.text(failure, { fallback: t('stations.client.updateNow.refused.failed') }),
        ),
    });
  };

  return {
    name,
    open,
    reason,
    ask: (why: UpdateNowReason = 'install_now') => {
      setError(null);
      setReason(why);
      setOpen(true);
    },
    close: () => {
      setOpen(false);
      backToAction();
    },
    triggerId,
    confirm,
    pending: send.isPending,
    error,
    progress,
    /** While a sent Update now is still moving: the client list is worth reading every few seconds. */
    following: progress?.following === true,
  };
}

export type UpdateNowState = ReturnType<typeof useUpdateNow>;

/**
 * The action, beside the client's status badge. Offline, it is off: a PC that is not connected cannot check. On a diskless
 * PC (`reason: update_image`) it reads "Update the image".
 */
export function UpdateNowButton({
  station,
  state,
  reason = 'install_now',
}: {
  readonly station: FloorStationState;
  readonly state: UpdateNowState;
  readonly reason?: UpdateNowReason;
}): ReactNode {
  const { t } = useTranslation('console');
  const copy = reason === 'update_image' ? 'stations.client.updateImage' : 'stations.client.updateNow';
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={<Icon name="download" />}
      disabled={!station.online}
      title={station.online ? t(`${copy}.actionNamed`, { name: state.name }) : t('stations.client.updateNow.offlineHint')}
      busy={state.pending}
      id={state.triggerId}
      aria-expanded={state.open}
      onClick={() => state.ask(reason)}
    >
      {t(`${copy}.action`)}
    </Button>
  );
}

/**
 * The confirm, in place inside the station modal (a dialog never opens a dialog, spec/13 §2): what will happen, then
 * Cancel or Update now. Focus moves to it when it opens, and back to the action when it closes.
 */
export function UpdateNowConfirm({ state }: { readonly state: UpdateNowState }): ReactNode {
  const { t } = useTranslation('console');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (state.open) ref.current?.querySelector<HTMLElement>('button')?.focus();
  }, [state.open]);
  if (!state.open) return null;
  const image = state.reason === 'update_image';
  const copy = image ? 'stations.client.updateImage.confirm' : 'stations.client.updateNow.confirm';
  const title = t(`${copy}.title`, { name: state.name });
  return (
    <div ref={ref} role="group" aria-label={title} className="console-update-now__confirm">
      {image ? (
        <Notice tone="info" title={title}>
          <p>{t(`${copy}.description`, { name: state.name })}</p>
          <ol className="console-update-now__points">
            <li>{t(`${copy}.step1`)}</li>
            <li>{t(`${copy}.step2`)}</li>
            <li>{t(`${copy}.step3`)}</li>
            <li>{t(`${copy}.step4`)}</li>
          </ol>
          <p>{t(`${copy}.notSuper`)}</p>
        </Notice>
      ) : (
        <Notice tone="info" title={title}>
          <p>{t(`${copy}.description`)}</p>
          <ul className="console-update-now__points">
            <li>{t(`${copy}.check`)}</li>
            <li>{t(`${copy}.session`)}</li>
            <li>{t(`${copy}.restart`)}</li>
          </ul>
        </Notice>
      )}
      {state.error === null ? null : (
        <Notice tone="danger" role="alert">
          {state.error}
        </Notice>
      )}
      <div className="console-update-now__actions">
        <Button variant="ghost" size="sm" onClick={state.close}>
          {t(`${copy}.cancel`)}
        </Button>
        <Button variant="primary" size="sm" icon={<Icon name="download" />} busy={state.pending} onClick={state.confirm}>
          {t(`${copy}.send`)}
        </Button>
      </div>
    </div>
  );
}

/** What the sent Update now is doing, in words, under the client's facts; and the offline hint before one is sent. */
export function UpdateNowStatus({ station, state }: { readonly station: FloorStationState; readonly state: UpdateNowState }): ReactNode {
  const { t } = useTranslation('console');
  const progress = state.progress;
  if (progress === null) {
    return station.online ? null : <p className="console-muted">{t('stations.client.updateNow.offlineHint')}</p>;
  }
  return (
    <Notice tone={progress.tone} role="status" className="console-update-now__progress">
      {t(`stations.client.updateNow.progress.${progress.key}`, {
        name: state.name,
        version: progress.version ?? t('stations.client.updateNow.progress.theUpdate'),
        detail: progress.detail ?? '',
      })}
    </Notice>
  );
}
